import { type EcomProduct } from '../types.ts';
import { pushElevarEvent } from './dataLayer.ts';
import { mapProducts, mapClickProducts, mapImpressions } from './productMapper.ts';
import {
    getElevarUserProperties,
    getElevarAuthUserProperties,
    hasPushedElevarAuthEvent,
    markElevarAuthEventPushed,
} from './userProperties.ts';
import {
    DEFAULT_CURRENCY_CODE,
    ELEVAR_LIST_SEARCH_RESULTS,
    type ElevarPurchaseActionField,
} from './types.ts';
import { toStr } from './helpers.ts';

type WithCurrency = { currencyCode?: string };

export const pushElevarUserData = ({
    cartTotal,
    cartProducts,
    currencyCode = DEFAULT_CURRENCY_CODE,
}: {
    cartTotal?: string;
    cartProducts?: ReadonlyArray<EcomProduct>;
} & WithCurrency = {}): void => {
    pushElevarEvent({
        event: 'dl_user_data',
        user_properties: getElevarUserProperties(),
        cart_total: cartTotal ?? '0.0',
        ecommerce: {
            currencyCode,
            cart_contents: { products: mapProducts(cartProducts, 'cart_contents') },
        },
    });
};

export const pushElevarSignUp = (): void => {
    if (hasPushedElevarAuthEvent('dl_sign_up')) {
        return;
    }
    const userProperties = getElevarAuthUserProperties();
    if (!userProperties) {
        return;
    }
    markElevarAuthEventPushed('dl_sign_up');
    pushElevarEvent({
        event: 'dl_sign_up',
        user_properties: userProperties,
    });
};

export const pushElevarLogin = (): void => {
    if (hasPushedElevarAuthEvent('dl_login')) {
        return;
    }
    const userProperties = getElevarAuthUserProperties();
    if (!userProperties) {
        return;
    }
    markElevarAuthEventPushed('dl_login');
    pushElevarEvent({
        event: 'dl_login',
        user_properties: userProperties,
    });
};

export const pushElevarViewItemList = ({
    products,
    currencyCode = DEFAULT_CURRENCY_CODE,
}: {
    products: ReadonlyArray<EcomProduct>;
} & WithCurrency): void => {
    pushElevarEvent({
        event: 'dl_view_item_list',
        user_properties: getElevarUserProperties(),
        ecommerce: {
            currencyCode,
            impressions: mapImpressions(products),
        },
    });
};

export const pushElevarViewSearchResults = ({
    products,
    currencyCode = DEFAULT_CURRENCY_CODE,
    list = ELEVAR_LIST_SEARCH_RESULTS,
}: {
    products: ReadonlyArray<EcomProduct>;
    list?: string;
} & WithCurrency): void => {
    pushElevarEvent({
        event: 'dl_view_search_results',
        user_properties: getElevarUserProperties(),
        ecommerce: {
            currencyCode,
            actionField: { list },
            impressions: mapImpressions(products),
        },
    });
};

export const pushElevarSelectItem = ({
    products,
    list,
    currencyCode = DEFAULT_CURRENCY_CODE,
}: {
    products: ReadonlyArray<EcomProduct>;
    list?: string;
} & WithCurrency): void => {
    pushElevarEvent({
        event: 'dl_select_item',
        user_properties: getElevarUserProperties(),
        ecommerce: {
            currencyCode,
            click: {
                actionField: { list: list ?? '', action: 'click' as const },
                products: mapClickProducts(products),
            },
        },
    });
};

export const pushElevarViewItem = ({
    products,
    list,
    currencyCode = DEFAULT_CURRENCY_CODE,
}: {
    products: ReadonlyArray<EcomProduct>;
    list?: string;
} & WithCurrency): void => {
    pushElevarEvent({
        event: 'dl_view_item',
        user_properties: getElevarUserProperties(),
        ecommerce: {
            currencyCode,
            detail: {
                actionField: { list: list ?? '', action: 'detail' as const },
                products: mapProducts(products, 'view_item'),
            },
        },
    });
};

export const pushElevarAddToCart = ({
    products,
    list,
    currencyCode = DEFAULT_CURRENCY_CODE,
}: {
    products: ReadonlyArray<EcomProduct>;
    list?: string;
} & WithCurrency): void => {
    pushElevarEvent({
        event: 'dl_add_to_cart',
        user_properties: getElevarUserProperties(),
        ecommerce: {
            currencyCode,
            add: {
                actionField: { list: list ?? '' },
                products: mapProducts(products, 'add_to_cart'),
            },
        },
    });
};

export const pushElevarRemoveFromCart = ({
    products,
    list,
    currencyCode = DEFAULT_CURRENCY_CODE,
}: {
    products: ReadonlyArray<EcomProduct>;
    list?: string;
} & WithCurrency): void => {
    pushElevarEvent({
        event: 'dl_remove_from_cart',
        user_properties: getElevarUserProperties(),
        ecommerce: {
            currencyCode,
            remove: {
                actionField: { list: list ?? '' },
                products: mapProducts(products, 'remove_from_cart'),
            },
        },
    });
};

export const pushElevarViewCart = ({
    products,
    cartTotal,
    currencyCode = DEFAULT_CURRENCY_CODE,
}: {
    products: ReadonlyArray<EcomProduct>;
    cartTotal?: string;
} & WithCurrency): void => {
    pushElevarEvent({
        event: 'dl_view_cart',
        user_properties: getElevarUserProperties(),
        ...(cartTotal !== undefined ? { cart_total: cartTotal } : {}),
        ecommerce: {
            currencyCode,
            actionField: {},
            impressions: mapImpressions(products, 'view_cart'),
        },
    });
};

const getLandingSite = (): string =>
    typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/';

const pushElevarCheckoutStep = (
    event: 'dl_begin_checkout' | 'dl_add_shipping_info' | 'dl_add_payment_info',
    step: string,
    {
        products,
        currencyCode = DEFAULT_CURRENCY_CODE,
    }: {
        products: ReadonlyArray<EcomProduct>;
    } & WithCurrency
): void => {
    pushElevarEvent({
        event,
        user_properties: getElevarUserProperties(),
        marketing: { landing_site: getLandingSite() },
        ecommerce: {
            currencyCode,
            checkout: {
                actionField: { step },
                products: mapProducts(products, 'checkout'),
            },
        },
    });
};

export const pushElevarBeginCheckout = (
    args: {
        products: ReadonlyArray<EcomProduct>;
    } & WithCurrency
): void => pushElevarCheckoutStep('dl_begin_checkout', '1', args);

export const pushElevarAddShippingInfo = (
    args: {
        products: ReadonlyArray<EcomProduct>;
    } & WithCurrency
): void => pushElevarCheckoutStep('dl_add_shipping_info', '2', args);

export const pushElevarAddPaymentInfo = (
    args: {
        products: ReadonlyArray<EcomProduct>;
    } & WithCurrency
): void => pushElevarCheckoutStep('dl_add_payment_info', '3', args);

export const pushElevarPurchase = ({
    products,
    actionField,
    currencyCode = DEFAULT_CURRENCY_CODE,
}: {
    products: ReadonlyArray<EcomProduct>;
    actionField: ElevarPurchaseActionField;
} & WithCurrency): void => {
    pushElevarEvent({
        event: 'dl_purchase',
        user_properties: getElevarUserProperties(),
        marketing: { landing_site: getLandingSite() },
        ecommerce: {
            currencyCode,
            purchase: {
                actionField,
                products: mapProducts(products, 'purchase'),
            },
        },
    });
};

export const pushElevarSubscribe = (email?: string): void => {
    const customerEmail = toStr(email) ?? toStr(getElevarUserProperties().customer_email);
    if (!customerEmail) {
        return;
    }
    pushElevarEvent({
        event: 'dl_subscribe',
        lead_type: 'email',
        user_properties: { customer_email: customerEmail },
    });
};
